import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import { WheelPicker } from '../../components/common/wheel-picker/WheelPicker';
import { appTheme, type ThemeColors, useThemeStore } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { useRtl } from '../../shared/i18n/useRtl';
import {
  JOURNEY_AGE_MAX,
  JOURNEY_AGE_MIN,
  JOURNEY_HEIGHT_MAX,
  JOURNEY_HEIGHT_MIN,
  JOURNEY_WEIGHT_MAX,
  JOURNEY_WEIGHT_MIN,
  type JourneyGender,
} from '../../data/domains/journey';
import {
  createJourneyAgeWheelTheme,
  createJourneyHeightWheelTheme,
  createJourneyWeightWheelTheme,
} from './journeyWheelTheme';
import { journeyPanelEntering } from '../../shared/theme/studio/journeyMotion';
import { JourneyStepIntro } from './JourneyStepIntro';
import { SegmentedControl } from '../../components/common/SegmentedControl';
import {
  DASH_STUDIO_RADIUS,
  studioCardShell,
  androidFlatSurfaceStyle,
  androidScaleFont,
  androidScaleSize,
  studioSectionLabelStyle,
} from '../../shared/theme/studio/stackScreenShell';

const IS_ANDROID = Platform.OS === 'android';
const ANDROID_TEXT_COMPACT: TextStyle = IS_ANDROID ? { includeFontPadding: false } : {};

type Props = {
  colors: ThemeColors;
  age: number;
  gender: JourneyGender;
  weight: number;
  height: number;
  born_in_year_text: string;
  on_age_change: (years: number) => void;
  on_gender_change: (gender: JourneyGender) => void;
  on_weight_change: (kg: number) => void;
  on_height_change: (cm: number) => void;
  on_wheel_gesture_active_change?: (active: boolean) => void;
};

export const JourneyAboutStep = memo(function JourneyAboutStep({
  colors,
  age,
  gender,
  weight,
  height,
  born_in_year_text,
  on_age_change,
  on_gender_change,
  on_weight_change,
  on_height_change,
  on_wheel_gesture_active_change,
}: Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const appearance = useThemeStore(s => s.appearance);
  const sf = androidScaleFont;
  const styles = useMemo(() => createStyles(colors, sf), [colors]);
  const section_label = useMemo(() => studioSectionLabelStyle(colors), [colors]);

  const age_wheel = useMemo(() => createJourneyAgeWheelTheme(colors, appearance), [colors, appearance]);
  const weight_wheel = useMemo(() => createJourneyWeightWheelTheme(colors, appearance), [colors, appearance]);
  const height_wheel = useMemo(() => createJourneyHeightWheelTheme(colors, appearance), [colors, appearance]);

  return (
    <View style={styles.root}>
      <JourneyStepIntro colors={colors} title={t('journey.aboutYou')} subtitle={t('journey.usedToPersonalize')} />

      <Animated.View entering={journeyPanelEntering(0)} style={styles.panel}>
        <Text style={[section_label, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
          {t('journey.step2sectionAge')}
        </Text>
        <Text
          style={[
            styles.helper_line,
            { writingDirection: rtl.writingDirection, color: hexToRgba(colors.primary, 0.85) },
          ]}>
          {born_in_year_text}
        </Text>
        <WheelPicker
          {...age_wheel}
          valueMin={JOURNEY_AGE_MIN}
          valueMax={JOURNEY_AGE_MAX}
          value={age}
          onValueChange={on_age_change}
          onSliderGestureActiveChange={on_wheel_gesture_active_change}
          testID="journey-age-wheel"
          containerStyle={[
            styles.metric_wheel,
            rtl.layoutLtrStyle,
            { backgroundColor: hexToRgba(colors.primary, appearance === 'dark' ? 0.14 : 0.05) },
          ]}
          readoutGroupStyle={styles.metric_readout}
          trailingReadout={
            <Text
              style={[styles.wheel_unit, { color: colors.primary, writingDirection: rtl.writingDirection }]}
              accessibilityElementsHidden>
              {t('journey.years')}
            </Text>
          }
        />
      </Animated.View>

      <Animated.View entering={journeyPanelEntering(1)} style={styles.panel}>
        <Text style={[section_label, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
          {t('journey.gender')}
        </Text>
        <SegmentedControl
          colors={colors}
          value={gender}
          onChange={on_gender_change}
          options={[
            { value: 'male', label: t('journey.male') },
            { value: 'female', label: t('journey.female') },
          ]}
          testID="journey-gender"
        />
      </Animated.View>

      <Animated.View entering={journeyPanelEntering(2)} style={styles.panel}>
        <Text style={[section_label, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
          {t('journey.step2sectionWeight')}
        </Text>
        <WheelPicker
          {...weight_wheel}
          valueMin={JOURNEY_WEIGHT_MIN}
          valueMax={JOURNEY_WEIGHT_MAX}
          value={weight}
          onValueChange={on_weight_change}
          onSliderGestureActiveChange={on_wheel_gesture_active_change}
          testID="journey-weight-wheel"
          containerStyle={[
            styles.metric_wheel,
            rtl.layoutLtrStyle,
            { backgroundColor: hexToRgba(colors.success, appearance === 'dark' ? 0.14 : 0.05) },
          ]}
          readoutGroupStyle={styles.metric_readout}
          trailingReadout={
            <Text
              style={[styles.wheel_unit, { color: colors.success, writingDirection: rtl.writingDirection }]}
              accessibilityElementsHidden>
              {t('journey.kg')}
            </Text>
          }
        />
      </Animated.View>

      <Animated.View entering={journeyPanelEntering(3)} style={[styles.panel, styles.panel_last]}>
        <Text style={[section_label, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
          {t('journey.step2sectionHeight')}
        </Text>
        <WheelPicker
          {...height_wheel}
          valueMin={JOURNEY_HEIGHT_MIN}
          valueMax={JOURNEY_HEIGHT_MAX}
          value={height}
          onValueChange={on_height_change}
          onSliderGestureActiveChange={on_wheel_gesture_active_change}
          testID="journey-height-wheel"
          metricUnitLabel={t('journey.cm')}
          containerStyle={[
            styles.metric_wheel,
            rtl.layoutLtrStyle,
            { backgroundColor: hexToRgba(colors.tertiary, appearance === 'dark' ? 0.14 : 0.05) },
          ]}
          readoutGroupStyle={styles.metric_readout}
        />
      </Animated.View>
    </View>
  );
});

function createStyles(c: ThemeColors, sf: (n: number) => number) {
  return StyleSheet.create({
    root: {
      width: '100%',
    },
    panel: {
      padding: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.md,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
    },
    panel_last: {
      marginBottom: appTheme.spacing.xs,
    },
    helper_line: {
      fontSize: sf(13),
      lineHeight: sf(18),
      textAlign: 'center',
      fontWeight: '600',
      marginBottom: appTheme.spacing.xs,
      ...ANDROID_TEXT_COMPACT,
    },
    metric_wheel: {
      marginTop: appTheme.spacing.xs,
      borderRadius: DASH_STUDIO_RADIUS,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xs,
      width: '100%',
      alignSelf: 'stretch',
    },
    metric_readout: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: IS_ANDROID ? androidScaleSize(44) : androidScaleSize(40),
      gap: androidScaleSize(8),
    },
    wheel_unit: {
      fontSize: sf(IS_ANDROID ? 15 : 16),
      lineHeight: sf(IS_ANDROID ? 20 : 22),
      fontWeight: '700',
      flexShrink: 0,
      letterSpacing: 0.2,
      ...ANDROID_TEXT_COMPACT,
    },
  });
}
